import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { useFormikContext } from 'formik';
import { Box, Card, Grid, Spinner, Text } from "theme-ui";
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import {
  Heading,
  TextInput,
  Form,
  Button,
  SingleSelect,
  DataList,
  Card as LumosCard,
  AlertInline
} from "@loanpal/lumos__core";
import { object, string } from "yup";
import goodLeapLogoOnDark from "../../goodleapplus/images/goodleap-white-orange.svg";
import Loading from "../../../utils/loading";
import {
  phone,
  phoneDigits,
  numberToMoney,
  numberToMoneyNoDecimal,
  moneyStringToNumber,
  validateMoneyInput
} from "../../../utils/format";
import { default as ErrorPage } from "../../../utils/error";
import { default as NotFoundPage } from "../../../utils/not-found";
import { isGoodLeapPlusDomain } from "../../../utils/is-goodleapplus-domain";
import {
  resetEmbeddedPageScroll,
  useEmbedIframeGuard,
} from "../../../utils/embed-iframe-guard";
import { LoanAmountInput, ApplyStatesInput, FormLoanAmountText } from "../../../utils/apply-loan-amount-input";
import {
  resolveApplyStateEntries,
  toStateSelectItems,
} from "../../../utils/shared-app-states";
import {
  notifyAdminErrorMessage,
  connectedOrgTokenErrorMessage,
} from "../../../utils/api";
import { getTokenWithRetry,
         getPayment as getPaymentApi,
         getCategories as getCategoriesApi,
         getLimitations as getLimitationsApi,
         hasUpstreamListPayload,
         getOfferRowId,
         getUserByEmail,
         postLoanShare as postLoanShareApi,
         buildGetOffersEffectKey,
         resetGetOffersEffectKey,
         resolveSharedAppOfferCatalogResilient,
         notifyOfferReplacementIfNeeded,
         sharedAppOffersEmptyErrorMessage,
         shouldRunGetOffersEffect,
         isSubmitOfferAllowed,
         submitOfferInvalidMessage,
} from "../../../utils/api";
import {
  checkAndResolveSubmittingUserWithRetry,
  notifySubmittingUserSubstitution,
  submittingUserCheckKey,
  submittingUserSubstituteNotFoundMessage,
} from "../../../utils/submitting-user";

import ReCAPTCHA from "react-google-recaptcha";
import { Loader } from '@aws-amplify/ui-react';

// api
import { API, graphqlOperation } from "aws-amplify";
import { getSharedApp } from "../../../graphql/queries";
import { createGoodLinks } from "../../../graphql/mutations";

import HOME_IMPROVEMENT from "../../../images/HI_1.png";
import ROOF from "../../../images/ROOF_1.png";
import SOLAR from "../../../images/SOLAR_1.png";
import BATTERY from "../../../images/BATTERY_1.png";
import REMODELING from "../../../images/REMODEL_1.png";

/** GoodLeap Financing Plus apply flow — maroon/gold accents on forms; banner uses purple gradient vs teal banner (#003439) */
const GLP_BRAND = {
  maroon: "#762123",
  maroonHover: "#5c1a1c",
  gold: "#f2b533",
  black: "#000000",
  white: "#ffffff",
};

const glpBannerLogoStyle = {
  width: 230,
  maxWidth: "min(90%, 280px)",
  margin: "0 auto",
  display: "block",
  padding: "12px 16px",
};

// import { install, gtag } from '@analytics/google-analytics';

const SyncSalesRepFields = ({ userData }) => {
  const { setFieldValue } = useFormikContext();
  useEffect(() => {
    if (!userData?.email) return;
    setFieldValue("salesRepFirstName", userData.firstName);
    setFieldValue("salesRepLastName", userData.lastName);
    setFieldValue("salesRepEmail", userData.email);
    setFieldValue("salesRepPhone", userData.phone);
  }, [userData, setFieldValue]);
  return null;
};

const Home = () => {
  const [token, setToken] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingPayment, setLoadingPayment] = useState(true);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [error, setError] = useState({error: false, notifySlack: false});
  const [errorMessage, setErrorMessage] = useState("");
  const [notFound, setNotFound] = useState({ error: false, status: ""});
  const { id, client, offerId } = useParams();
  const [SharedApp, setSharedApp] = useState();
  const [payment, setPayment] = useState();
  const [displayResult, setDisplayResult] = useState(true);
  const [, setStates] = useState();
  const [statesItems, setStatesItems] = useState([]);
  const [goodLink, setGoodLink] = useState();
  const [canApply, setCanApply] = useState(false);
  const [captchaResponse, setCaptchaResponse] = useState("");
  const [minAmount, setMinAmount] = useState();
  const [maxAmount, setMaxAmount] = useState();
  const [limitations, setLimitations] = useState();
  const [offers, setOffers] = useState();
  const [offerItems, setOfferItems] = useState([])
  const [singleOffer, setSingleOffer] = useState("");
  const [paymentAmount, setPaymentAmount] = useState({});
  const [singleState, setSingleState] = useState("");
  const [offerDesc, setOfferDesc] = useState();
  const [offerName, setOfferName] = useState();
  const phoneRegExp = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
  const captchaRef = useRef(null);
  const formCardRef = useRef(null);
  const [elevatedToken, setElevatedToken] = useState(null);
  const [userData, setUserData] = useState({firstName: "", lastName: "", email: "", phone: ""});
  const [salesRepUnauthorizedWarning, setSalesRepUnauthorizedWarning] = useState(false);
  /** Ignore stale getOffers responses when SharedApp/token/effect re-fires. */
  const getOffersFetchIdRef = useRef(0);
  const getOffersEffectKeyRef = useRef("");
  const getUserDataFetchIdRef = useRef(0);
  const elevatedTokenFetchIdRef = useRef(0);
  /** Dedupe Slack when connected-org token fails for the same app. */
  const connectedOrgNotifyRef = useRef("");
  const prevSharedAppIdRef = useRef(undefined);
  const elevatedTokenEffectKeyRef = useRef("");
  const elevatedTokenSuccessRef = useRef(false);
  /** Set when getOffers succeeds; suppresses false-positive offer-fetch Slack. */
  const getOffersSuccessRef = useRef(false);
  const offerReplacementNotifyRef = useRef("");
  const submitOfferContextRef = useRef({
    channelOfferIds: [],
    configuredOfferIds: [],
  });
  /** Dedupe Slack when offer catalog fetch fails for the same app. */
  /** In-flight guard for parallel submitting-user checks (e.g. Strict Mode). */
  const submittingUserCheckInFlightRef = useRef("");
  /** One submitting-user check per SharedApp (+ token when connected org). */
  const submittingUserCheckKeyRef = useRef("");
  const [checkingSubmittingUser, setCheckingSubmittingUser] = useState(false);
  const { isEmbedded } = useEmbedIframeGuard();

const version = "2.0";

/* Query String parameters
*  values: ?cost=18000&lname=Sanchez&fname=Hector&phone=650-207-7716&email=hsanchez@goodleap.com&loanterm=42a10ba5-a352-429a-ba3d-c84cb44cd9e3&state=AZ&locked=true&salesRepEmail=hsanchez@goodleap.com
*
*  salesRepEmail: the email of the sales rep to be used for the application.  If not provided, the default user will be used.
*/

  // Prepopulate with query string paramenters
  const [ searchParams ] = useSearchParams();
  const salesRepEmailParam = searchParams.get("salesRepEmail")?.trim() || "";
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    phone: '',
    offerId: '',
    locked: false,
    productType: '',
    state: '',
    refnum: '',
    cost: '',
    channel: ''
  });

  useEffect(() => {
    const id = "goodleapplus-google-font-lato";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;0,900;1,400&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!SharedApp || !salesRepEmailParam) return;

    const needsElevatedToken =
      SharedApp.useConnectedOrgs &&
      SharedApp.parentOrganizationClientId &&
      SharedApp.organizationId;

    if (needsElevatedToken && !elevatedToken) return;

    getUserData(
      salesRepEmailParam,
      needsElevatedToken ? elevatedToken.token : undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SharedApp, elevatedToken, salesRepEmailParam]);

  const salesRepFromUserRecord = (user) => ({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phoneNumber?.number || user?.phones?.[0]?.number || "",
  });

  const salesRepFromFormOrSharedApp = (values) => ({
    firstName:
      userData?.email
        ? userData.firstName
        : values?.salesRepFirstName || SharedApp?.submittingUser?.firstName || "",
    lastName:
      userData?.email
        ? userData.lastName
        : values?.salesRepLastName || SharedApp?.submittingUser?.lastName || "",
    email:
      userData?.email
        ? userData.email
        : values?.salesRepEmail || SharedApp?.submittingUser?.email || "",
    phone:
      userData?.email
        ? userData.phone
        : values?.salesRepPhone || SharedApp?.submittingUser?.phone || "",
  });

  const getUserData = async (email, token) => {
    const fetchId = ++getUserDataFetchIdRef.current;
    const isStale = () => fetchId !== getUserDataFetchIdRef.current;
    console.log("Getting user data for", email);
    setSalesRepUnauthorizedWarning(false);
    try {
      const response = await getUserByEmail(SharedApp.clientId, email, token);
      if (isStale()) return;
      console.log("userData response", response);
      if (!response || !Array.isArray(response.data)) {
        return;
      }
      if (response.data.length === 0) {
        setSalesRepUnauthorizedWarning(true);
        return;
      }
      const nextUserData = salesRepFromUserRecord(response.data[0]);
      setUserData(nextUserData);
      console.log("userData", nextUserData);
    } catch (err) {
      if (isStale()) return;
      console.log("error getting user data", err);
    }
  }

  useEffect(() => {
    setFormData({
      fname: searchParams.get('fname') || '',
      lname: searchParams.get('lname') || '',
      email: searchParams.get('email') || '',
      phone: searchParams.get('phone') || '',
      locked: searchParams.get('locked') === "true" || searchParams.get('locked') === "false" ? JSON.parse(searchParams.get('locked')) : false,
      prodType: searchParams.get('producttype') || '',
      state: searchParams.get('state') || '',
      refnum: searchParams.get('refnum') || '',
      offerId: searchParams.get('loanterm') || '',
      cost: searchParams.get('cost') || '',
      channel: searchParams.get('channel') || ''
    });
  }, [searchParams]);

  useEffect(() => {
    fetchSharedApp(id);
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
      if (paymentAmount.payment) {
        getPayment(singleOffer, paymentAmount.amount);
      }
    // eslint-disable-next-line
  }, [paymentAmount])

  const getElevatedToken = useCallback(async (sharedApp, parentOrganizationClientId, organizationId) => {
    const fetchId = ++elevatedTokenFetchIdRef.current;
    const isStale = () => fetchId !== elevatedTokenFetchIdRef.current;

    const reportFailure = async () => {
      await new Promise((resolve) => setTimeout(resolve, 800));
      if (isStale()) return;
      if (elevatedTokenSuccessRef.current) return;

      const msg = connectedOrgTokenErrorMessage(
        parentOrganizationClientId,
        organizationId
      );
      const notifyKey = `${sharedApp?.id ?? id}|${parentOrganizationClientId}|${organizationId}`;
      setElevatedToken(null);
      setErrorMessage(msg);
      setError({ error: true, notifySlack: false });
      if (connectedOrgNotifyRef.current === notifyKey) return;
      connectedOrgNotifyRef.current = notifyKey;
      console.log("Connected org token failed, notifying Slack", notifyKey);
      await notifyAdminErrorMessage(sharedApp, sharedApp?.id ?? id, msg, true);
    };

    try {
      elevatedTokenSuccessRef.current = false;
      const tokenResult = await getTokenWithRetry(
        parentOrganizationClientId,
        organizationId
      );
      if (isStale()) return;
      if (tokenResult?.token) {
        console.log("Elevated token received", tokenResult);
        elevatedTokenSuccessRef.current = true;
        setElevatedToken(tokenResult);
        setError({ error: false, notifySlack: false });
        setErrorMessage("");
        return;
      }
      console.log("No token received for connected org after retries");
      await reportFailure();
    } catch (err) {
      console.log("error getting token for connected org", err);
      await reportFailure();
    }
  }, [id]);

  /** Drop prior org token when navigating to a different SharedApp (not on first load). */
  useEffect(() => {
    const nextId = SharedApp?.id;
    if (nextId == null) return;

    const prevId = prevSharedAppIdRef.current;
    prevSharedAppIdRef.current = nextId;

    if (prevId === undefined) {
      setElevatedToken(null);
      return;
    }
    if (prevId === nextId) return;

    getOffersFetchIdRef.current += 1;
    getUserDataFetchIdRef.current += 1;
    elevatedTokenFetchIdRef.current += 1;
    connectedOrgNotifyRef.current = "";
    elevatedTokenEffectKeyRef.current = "";
    elevatedTokenSuccessRef.current = false;
    getOffersSuccessRef.current = false;
    offerReplacementNotifyRef.current = "";
    submitOfferContextRef.current = {
      channelOfferIds: [],
      configuredOfferIds: [],
    };
    submittingUserCheckInFlightRef.current = "";
    submittingUserCheckKeyRef.current = "";
    setCheckingSubmittingUser(false);
    resetGetOffersEffectKey(getOffersEffectKeyRef);
    setElevatedToken(null);
    setUserData({ firstName: "", lastName: "", email: "", phone: "" });
  }, [SharedApp?.id]);

  const sharedAppId = SharedApp?.id;
  const sharedAppClientId = SharedApp?.clientId ?? "";
  const sharedAppFormUrl = SharedApp?.formUrl ?? "";
  const useConnectedOrgs = SharedApp?.useConnectedOrgs ?? false;
  const parentOrganizationClientId = SharedApp?.parentOrganizationClientId ?? "";
  const organizationId = SharedApp?.organizationId ?? "";

  useEffect(() => {
    if (!useConnectedOrgs || !parentOrganizationClientId || !organizationId || !sharedAppId) {
      return;
    }
    const effectKey = `${sharedAppId}|${parentOrganizationClientId}|${organizationId}`;
    if (elevatedTokenEffectKeyRef.current === effectKey) return;
    elevatedTokenEffectKeyRef.current = effectKey;

    console.log("Getting elevated token");
    getElevatedToken(
      {
        id: sharedAppId,
        clientId: sharedAppClientId,
        formUrl: sharedAppFormUrl,
        parentOrganizationClientId,
        organizationId,
      },
      parentOrganizationClientId,
      organizationId
    );
  }, [
    sharedAppId,
    useConnectedOrgs,
    parentOrganizationClientId,
    organizationId,
    sharedAppClientId,
    sharedAppFormUrl,
    getElevatedToken,
  ]);

  useEffect(() => {
    if (!SharedApp) return;
    const needsElevatedToken =
      SharedApp.useConnectedOrgs &&
      SharedApp.parentOrganizationClientId &&
      SharedApp.organizationId;
    if (needsElevatedToken && !elevatedToken) return;

    const effectKey = buildGetOffersEffectKey(SharedApp, elevatedToken);
    if (!shouldRunGetOffersEffect(getOffersEffectKeyRef, effectKey)) return;

    setPaymentAmount({ amount: SharedApp.loanAmount, payment: false });
    getOffers(SharedApp.clientId, SharedApp.channel, SharedApp.loanAmount);
    getCategories(SharedApp.clientId, SharedApp.category);
    // eslint-disable-next-line
  }, [SharedApp, elevatedToken]);

  useEffect(() => {
    const submittingUserEmail = SharedApp?.submittingUser?.email?.trim();
    if (!SharedApp?.id || !submittingUserEmail) return;

    const needsElevatedToken =
      SharedApp.useConnectedOrgs &&
      SharedApp.parentOrganizationClientId &&
      SharedApp.organizationId;
    if (needsElevatedToken && !elevatedToken) return;

    const tokenPart = needsElevatedToken
      ? String(elevatedToken?.token ?? "")
      : "no-token";
    const checkKey = submittingUserCheckKey(
      SharedApp.id,
      tokenPart,
      submittingUserEmail
    );
    if (submittingUserCheckKeyRef.current === checkKey) return;
    if (submittingUserCheckInFlightRef.current === checkKey) return;
    submittingUserCheckInFlightRef.current = checkKey;

    checkSubmittingUser(SharedApp, submittingUserEmail, checkKey, tokenPart).finally(
      () => {
        if (submittingUserCheckInFlightRef.current === checkKey) {
          submittingUserCheckInFlightRef.current = "";
        }
      }
    );
    // eslint-disable-next-line
  }, [SharedApp, elevatedToken]);

  useEffect( () => {
    try {
      if (goodLink){
        console.log("This is a goodLink", goodLink);
        API.graphql(
          graphqlOperation(createGoodLinks, { input: goodLink })
        );
      }
    } catch (err) {
      console.log("error fetching Shared Apps");
    }
  }, [goodLink]);

  useEffect(() => {
    if (error.error && errorMessage !== "") {
      notifyAdminErrorMessage(SharedApp, id, errorMessage, error.notifySlack, {
        blocking: true,
      });
    }
    // eslint-disable-next-line
  }, [error, errorMessage]);

const checkSubmittingUser = async (
  sharedApp,
  submittingUserEmail,
  checkKey,
  tokenPart = "no-token"
) => {
  if (!sharedApp || !submittingUserEmail) return;

  const token = elevatedToken ? elevatedToken.token : undefined;

  setCheckingSubmittingUser(true);
  try {
    console.log("checking submitting user", submittingUserEmail);
    const result = await checkAndResolveSubmittingUserWithRetry(
      sharedApp.clientId,
      submittingUserEmail,
      token
    );

    if (result.status === "inconclusive") {
      console.log("Submitting user check inconclusive — skipping Slack", result.reason);
      return;
    }

    if (checkKey) {
      submittingUserCheckKeyRef.current = checkKey;
    }

    if (result.status === "configured") {
      return;
    }

    if (result.status === "not_found") {
      if (!result.confident) {
        console.log("Submitting user not_found but not confident — skipping Slack");
        return;
      }
      setErrorMessage(submittingUserSubstituteNotFoundMessage);
      setError({ error: true, notifySlack: true });
      return;
    }

    const { substitute, roleName } = result;
    const nextApp = { ...sharedApp, submittingUser: substitute };
    submittingUserCheckKeyRef.current = submittingUserCheckKey(
      sharedApp.id,
      tokenPart,
      substitute.email
    );
    setSharedApp(nextApp);

    console.log("Submitting user substituted, notifying Slack", {
      id: sharedApp.id,
      configured: submittingUserEmail,
      substitute: substitute.email,
    });
    await notifySubmittingUserSubstitution(
      nextApp,
      sharedApp.id ?? id,
      submittingUserEmail,
      substitute.email,
      roleName
    );
  } catch (err) {
    console.log("error checking submitting user", err);
  } finally {
    setCheckingSubmittingUser(false);
  }
};

const productItems = [
    { label: 'Loan product', text: offerDesc},
    {
      label: 'Term and rate',
      text: offerName,
    }
  ];

const estimatedPayment = payment ? payment[0].calculations.map((pay) => ({
    label: pay.paymentDescription,
    text: <div>
          {loadingPayment ? (
            <SkeletonTheme baseColor="#d7d7d7" highlightColor="#f5f5f5">
              <Text variant="h3" as="h2">
                $ <Skeleton width={125} height={24} />
              </Text>
            </SkeletonTheme>
          ) : (
            <Text variant="h3" as="h2">
              {pay.amount === null ? `${numberToMoney(pay.minimumAmount.value)} - ${numberToMoney(pay.maximumAmount.value)}` : numberToMoney(pay.amount.value)}
              <span style={styles.period} color="neutral.base">
              &nbsp;{pay.interval === "MONTHLY" ? "/ month" : ""}
              </span>
            </Text>
          )}
          </div>,
  })) : {};

    const getPayment = async (offerId, loanAmount) => {
    setLoadingPayment(true);
    if (offerId === "" || loanAmount === "" || loanAmount <= minAmount || loanAmount > maxAmount || !validateMoneyInput(loanAmount)) {
      setLoadingPayment(false);
      return;
    }
    try {
      const clientId = SharedApp.clientId;
      await getPaymentApi(clientId, offerId, loanAmount, ["AUTOPAY"], elevatedToken ? elevatedToken.token : undefined)
      .then((response) => {
        setPayment(response);
        setLoadingPayment(false);
      },
      (err) => {
        setErrorMessage("Error getting payments " + err);
        setError({error:true, notifySlack: true});
        console.log("Error getting payments ", err);
        setLoadingPayment(false);
      })

    } catch (err) {
      console.log("error getting payments");
      setLoadingPayment(false);
      return false;
    }
  };

  const getCategories = async (clientId, category) => {
    try {
      const response = await getCategoriesApi(
        clientId,
        elevatedToken ? elevatedToken.token : undefined
      );
      if (!response || !Array.isArray(response)) {
        console.log("Categories not available (non-fatal)");
        return;
      }
      const cat = response.find(
        (el) => el.name === getCategoryName(category).toUpperCase()
      );
      if (!cat) {
        console.log("Category not found in catalog (non-fatal)", category);
        return;
      }
      getLimitations(clientId, cat.id);
      setMinAmount(cat.minLoanAmount);
      setMaxAmount(cat.maxLoanAmount);
    } catch (err) {
      console.log("error getting categories", err);
      return false;
    }
  };
  const getLimitations = async (clientId, category) => {
    if (!category) return;
    try {
      const response = await getLimitationsApi(
        clientId,
        elevatedToken ? elevatedToken.token : undefined
      );
      if (!hasUpstreamListPayload(response)) {
        console.log("Limitations not available (non-fatal)");
        return;
      }
      const limitations = response.data.filter(
        (element) => element.categoryId === category
      );
      setLimitations(limitations);
    } catch (err) {
      console.log("error getting limitations", err);
      return false;
    }
  };

    const getOffers = async (clientId, channel, loanAmount, state) => {
    const fetchId = ++getOffersFetchIdRef.current;
    const isStale = () => fetchId !== getOffersFetchIdRef.current;
    try {
      const catalogResult = await resolveSharedAppOfferCatalogResilient(
        {
          clientId,
          channel,
          offers: SharedApp.offers,
          useConnectedOrgs: SharedApp.useConnectedOrgs,
          parentOrganizationClientId: SharedApp.parentOrganizationClientId,
          organizationId: SharedApp.organizationId,
          token: elevatedToken ? elevatedToken.token : null,
          sharedAppId: SharedApp.id ?? id,
        },
        {
          shouldAbort: () => isStale() || getOffersSuccessRef.current,
        }
      );
      if (isStale()) return false;

      if (catalogResult.outcome === "fetch_failed") {
        if (isStale()) return false;
        if (getOffersSuccessRef.current) return false;
        const msg = "Error getting offers, offer fetch failed";
        console.log("Offer catalog fetch failed after retries (transient)", {
          sharedAppId: SharedApp?.id ?? id,
          clientId,
          channel,
        });
        setErrorMessage(msg);
        setError({ error: true, notifySlack: false });
        return false;
      }

      let data = catalogResult.data;
      if (data.length === 0) {
        if (isStale()) return false;
        setOffers([]);
        setOfferItems([]);
        setErrorMessage(
          sharedAppOffersEmptyErrorMessage(catalogResult.configuredIds)
        );
        setError({ error: true, notifySlack: false });
        console.log("Error getting offers, no offers returned");
        return false;
      }

      if (offerId !== undefined) {
        const so = data.find((o) => getOfferRowId(o) === offerId);
        data = so ? [so] : [];
      }
      if (data.length > 0) {
        if (isStale()) return false;
        await notifyOfferReplacementIfNeeded(
          SharedApp,
          SharedApp?.id ?? id,
          catalogResult,
          offerReplacementNotifyRef
        );
        getOffersSuccessRef.current = true;
        submitOfferContextRef.current = {
          channelOfferIds: catalogResult.channelOfferIds ?? [],
          configuredOfferIds: catalogResult.configuredIds ?? [],
        };
        setError({ error: false, notifySlack: false });
        setErrorMessage("");
        setOffers(data);
        let offersArrayOptions = [];
        offersArrayOptions = data
          .map((element) => [
            ...offersArrayOptions,
            { name: element.productCode.includes("DEFERRED") ? `${element.productName} - ${element.name}` : `${element.name} - ${element.productName}`, value: getOfferRowId(element) },
          ])
          .flat();
        setOfferItems(offersArrayOptions);
        if (data.length === 1) {
          setSingleOffer(getOfferRowId(data[0]));
          setOfferDesc(data[0].productDescription);
          setOfferName(data[0].name);
          let stateArrayOptions = [];
          const resolvedStates = resolveApplyStateEntries(
            SharedApp.states,
            data[0].states
          );
          setStates(JSON.stringify(resolvedStates));
          if (resolvedStates.length === 1) {
            setSingleState(resolvedStates[0]);
          }
          stateArrayOptions = toStateSelectItems(resolvedStates);
          setStatesItems(stateArrayOptions);
          getPayment(getOfferRowId(data[0]), loanAmount);
        } else if (searchParams.get('loanterm') && data.length > 1) {
          const offerParam = data.find((o) => getOfferRowId(o) === searchParams.get('loanterm'));
          if (offerParam) {
            setSingleOffer(getOfferRowId(offerParam));
            setOfferDesc(offerParam.productDescription);
            setOfferName(offerParam.name);
            setStates(JSON.stringify(offerParam.states));
            if (state) {
              setSingleState(state);
            } else if (offerParam.states.length === 1) {
              setSingleState(offerParam.states[0]);
            } else if (offerParam.states.length > 1 && offerParam.states.find((st) => st === formData.state) !== undefined) {
              setSingleState(formData.state);
            }
            let stateArrayOptions = [];
            stateArrayOptions = offerParam.states
              .map((element) => [
                ...stateArrayOptions,
                { name: element, value: element },
              ])
              .flat();
            setStatesItems(stateArrayOptions);
            getPayment(getOfferRowId(offerParam), loanAmount);
          }
        }
      } else {
        if (isStale()) return false;
        setErrorMessage("Error getting offers, no offers returned");
        setError({ error: true, notifySlack: false });
        console.log("Error getting offers, no offers returned after loanterm filter");
      }
    } catch (err) {
      if (isStale()) return false;
      setErrorMessage("Error getting offers " + err);
      setError({ error: true, notifySlack: true });
      console.log("error getting Offers", err);
      return false;
    }
  };

  async function fetchSharedApp(id) {
    try {
      const appData = await API.graphql(
        graphqlOperation(getSharedApp, { id: id })
      );
      const { clientId: cId, status: st } = appData.data.getSharedApp;
      const displayStatuses = ["ACTIVE", "TEST"];
      const clientInPath = client != null && String(client).trim() !== "";
      const clientMismatch =
        clientInPath &&
        cId.toLowerCase() !== String(client).trim().toLowerCase();
      if (clientMismatch || displayStatuses.indexOf(st) < 0) {
        setLoading(false);
        setNotFound({ error: true, status: st });
        return null;
      }
      if (searchParams.get('cost') && searchParams.get('cost') !== "") {
        appData.data.getSharedApp.loanAmount = searchParams.get('cost');
      }

      setSharedApp(appData.data.getSharedApp);
      // analytics tracking
      // install("G-KBMPQSEG5M", {
      //   page_title: `GoodLeap GoodLink ${appData.data.getSharedApp.clientId}`,
      //   page_location: `https://${window.location.host}/apply/${id}`,
      // });
      // gtag("set", "clientId", appData.data.getSharedApp.clientId);
      // gtag("event", "page_view");
      setDisplayResult(JSON.parse(appData.data.getSharedApp.displayIframe));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setNotFound({ error: true, status: "ERROR" });
      console.log("error fetching SharedApp", err);
    }
  }

  const handleClick = async (payload) => {
    try {
      setSpinner(true);
      const freshCaptcha = captchaRef.current?.getValue?.() || captchaResponse;
      const data = await postLoanShareApi(
        SharedApp.clientId,
        payload,
        elevatedToken ? elevatedToken.token : undefined,
        {
          sharedAppId: SharedApp.id ?? id,
          captchaResponse: freshCaptcha,
        }
      );
      setIframeLoading(true);
      setTimeout(() => setIframeLoading(false), 5000);
      setToken(data.link);
      if (data.link !== "") setSpinner(false);
    } catch (err) {
      setSpinner(false);
      setErrorMessage("Error creating Shared App " + payload);
      setError({error:true, notifySlack: true});
      console.log("error creating Shared App", err);
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaResponse(value || "");
    setCanApply(!!value);
  };

  const handleCaptchaExpired = () => {
    setCaptchaResponse("");
    setCanApply(false);
    captchaRef.current?.reset?.();
  };

  const buildGoodLink = (values) => {
    if (values.email.includes("@goodleap.com")) return null;
    const gLink = {
      clientId: SharedApp.clientId,
      custFirstName: values.firstName,
      custLastName: values.lastName,
      custEmail: values.email,
      custMobile: values.mobile,
      custState: values.state,
      custLoanAmount: values.loanAmount,
      offerId: values.offer,
      emailSent: SharedApp.sendEmail,
    };
    console.log(gLink);
    setGoodLink(gLink);
  }

  const getReferenceId = (refId, mobile, lastName) => {
    console.log(refId);
    return refId === "lastName" ? lastName
           : refId === "mobilePhone" ? phoneDigits(mobile)
           : refId === "quickLink" ? "QuickLink"
           : refId !== "" ? SharedApp.referenceId
           : refId === "goodleapplus" ? "GoodLeapPlus"
           : ""
  }

  const buildPaydown = (values) => {
    if (
      !values.firstName ||
      !values.lastName ||
      !values.email ||
      !values.state ||
      !values.mobile ||
      !values.offer
    ) {
      return;
    }
    if (!isSubmitOfferAllowed(values.offer, submitOfferContextRef.current)) {
      return;
    }
    buildGoodLink(values);
    const {
      referenceId,
      offer,
      channel,
      state,
      email,
      firstName,
      lastName,
      mobile,
      loanAmount,
      autopayIntent,
      sendEmail,
    } = values;
    const salesRep = salesRepFromFormOrSharedApp(values);

    return JSON.stringify({
      referenceNumber: getReferenceId(referenceId, mobile, lastName),
      offerId: offer,
      channel: channel,
      state: state,
      applicant: {
        email: email,
        firstName: firstName,
        lastName: lastName,
      },
      submittingUser: {
        firstName: salesRep.firstName,
        lastName: salesRep.lastName,
        email: salesRep.email.trim(),
        phone: salesRep.phone,
      },
      amount: {
        value: loanAmount,
        type: "USD",
      },
      sendEmail: sendEmail || false,
      enrollments: [autopayIntent ? "AUTOPAY" : ""],
    });
  };

  const getCategory = (category) => {
    if (category === "HOME_IMPROVEMENT") return HOME_IMPROVEMENT
    else if (category === "ROOF") return ROOF;
    else if (category === "SOLAR") return SOLAR;
    else if (category === "BATTERY") return BATTERY;
    else if (category === "REMODELING") return REMODELING;
    else return "";
  }

  const getCategoryName = (category) => {
    if (category === "HOME_IMPROVEMENT") return "Home Improvement";
    else if (category === "ROOF") return "Roofing / Windows / Doors / Siding / Geothermal / Restoration";
    else if (category === "SOLAR") return "Solar";
    else if (category === "BATTERY") return "Battery";
    else if (category === "REMODELING") return "Remodeling";
    else return "";
  }

  const fieldLocked = (lockedByQuery) =>
    checkingSubmittingUser || lockedByQuery;

  const glpPageShellSx = {
    fontFamily: "'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    color: GLP_BRAND.black,
    backgroundColor: GLP_BRAND.white,
    minHeight: "100vh",
  };

  const goodLeapPlusBannerNode = (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "960px",
        mt: 0,
        mb: 0,
        mx: "auto",
        p: "24px",
        lineHeight: 0,
        background:
          "linear-gradient(135deg, #200F51 0%, #0D0830 60%, #3A0D5C 100%)",
        borderBottom: `4px solid ${GLP_BRAND.gold}`,
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Heading
        className="heading-box"
        sx={{
          width: "100%",
          maxWidth: "960px",
          margin: "0 auto",
          background: "transparent",
          padding: 0,
        }}
      >        
        <img src={goodLeapLogoOnDark} alt="GoodLeap Financing Plus" style={glpBannerLogoStyle} />
      </Heading>
    </Box>
  );

  const goodLeapPlusHeaderNode = (
    <>
      {goodLeapPlusBannerNode}
      {salesRepUnauthorizedWarning && (
        <Box sx={{ maxWidth: "960px", mx: "auto", pt: 2, pb: 3 }}>
          <AlertInline
            variant="warning"
            sxProps={{
              borderRadius: "8px",
              overflow: "hidden",
              border: "none",
              borderWidth: 0,
              lineHeight: 1.8
            }}
          >
            The sales rep email provided is not an authorized user in GoodLeap.
            <br />
            Please reach out to the Admin on your account for more details.
            <br />
            You can continue to submit the application using the default user: <strong>{SharedApp.submittingUser.firstName} {SharedApp.submittingUser.lastName}</strong> with email <strong>{SharedApp.submittingUser.email}</strong>.<br />            
          </AlertInline>
        </Box>
      )}
    </>
  );

  return (
    <Box sx={glpPageShellSx}>
      {error.error && <ErrorPage sharedApp={SharedApp} errorMessage={errorMessage} />}
      {notFound.error &&
        (isGoodLeapPlusDomain() ? (
          <Navigate to="/" replace />
        ) : (
          <NotFoundPage status={notFound.status} />
        ))}
      {loading && <Loading />}
      {token !== "" && displayResult && !loading && !error.error && !notFound.error && (
        <div className="container">
          {iframeLoading && <Loading />}
          <iframe
            title="GoodLeap Financing Plus"
            className="iframe"
            allowFullScreen
            src={token}
          />
        </div>
      )}
      {token !== "" && !displayResult && !loading && !error.error && !notFound.error && (
        <Box
          sx={{
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {goodLeapPlusHeaderNode}
          <Box sx={{ pl: 6, pr: 6, pt: 4, width: ["960px", "640px", "480px"] }}>
            <>
              <Heading variant="h2">
                Thank you for your interest in GoodLeap financing.
              </Heading>
              <Box sx={{ pt: 2, fontSize: "18px" }}>
                An application form has been sent to your email address.
                <br />
                <br />
                If you have questions, reach out to{" "}
                <Text sx={{ fontWeight: 800 }}>
                  {userData.email ? userData.firstName : SharedApp.submittingUser.firstName}{" "}
                  {userData.email ? userData.lastName : SharedApp.submittingUser.lastName}
                </Text>{" "}
                at{" "}
                <Text sx={{ fontWeight: 800 }}>
                  {phone(userData.email ? userData.phone : SharedApp.submittingUser.phone)}
                </Text>{" "}
                or{" "}
                <Text sx={{ fontWeight: 800 }}>
                  {userData.email ? userData.email : SharedApp.submittingUser.email}
                </Text>
                .
              </Box>
            </>
          </Box>
        </Box>
      )}
      {token === "" && !loading && !error.error && !notFound.error && (
        <Box
          sx={{
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {goodLeapPlusHeaderNode}
          {!offers && (
            <Form>
            <Card
              sx={{
                width: 960,
                margin: "0 auto",
              }}
              className="card-form"
            >
              <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                <Heading variant="h5">
                  Credit application: complete this form to see an ESTIMATED payment scenario. Use the dollar amount suggested by your company representative.
                </Heading>
                <Box as="p" sx={{ mb: 2 }}>
                  &nbsp;
                </Box>
                <Box sx={{ width: "100%", mb: 2, mt: 2  }}>
                  <Loader
                      variation="linear"
                      emptyColor="#ffffff"
                      filledColor={GLP_BRAND.gold}
                      className="loader"
                      />
                </Box>
                <Box>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <TextInput
                      name="firstName"
                      label="First name"
                      autoComplete="off"
                      readOnly
                    />
                    <TextInput
                      name="lastName"
                      label="Last name"
                      autoComplete="off"
                      readOnly
                    />
                  </Grid>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <TextInput
                      name="email"
                      label="Email address"
                      autoComplete="off"
                      type="email"
                      readOnly
                    />
                    <TextInput
                      name="mobile"
                      label="Mobile number"
                      autoComplete="off"
                      type="phone"
                      description="Please enter 10 digit mobile number"
                      readOnly
                    />
                  </Grid>
                   <Grid columns={[1, 2]} gap={[0, 3]}>
                      <>
                       <Box sx={{ width: "100%", mr: 5, mt: 2 }}>
                        <SingleSelect
                          name="offer"
                          label={"Getting offers, one moment..."}
                          readOnly
                          items={[]}
                        />
                      </Box>
                      <Box sx={{ width: "30%", mr: 5 }}>
                      <SingleSelect
                        name="state"
                        label={"State"}
                        readOnly
                        size={20}
                        items={[]}
                      />
                      </Box>
                    </>
                  </Grid>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <Box sx={{ mt: 2 }}>
                      <TextInput
                        name="loanAmount"
                        label="Amount to finance"
                        autoComplete="off"
                        type="loanAmount"
                        readOnly
                      />
                     </Box>
                  </Grid>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    my: 3,
                  }}
                >
                <Button type="submit" sx={{ mt: 3, px: 5 }} disabled>
                  APPLY NOW
                </Button>
                </Box>
              </Box>
            </Card>
            </Form>
          )}
          {offers && (
          <Form
            key={SharedApp?.submittingUser?.email ?? "apply-form"}
            initialValues={{
              firstName: formData.fname || "",
              lastName: formData.lname || "",
              email: formData.email || "",
              mobile: formData.phone || "",
              state: singleState,
              offer: singleOffer || "",
              channel: formData.channel ? formData.channel : SharedApp ? SharedApp.channel : "",
              referenceId: formData.referenceNumber ? formData.referenceNumber : SharedApp ? SharedApp.referenceId : "",
              loanAmount: formData.cost ? numberToMoney(formData.cost) : SharedApp ? numberToMoney(SharedApp.loanAmount) : numberToMoney(0.00),
              autopayIntent: true,
              sendEmail: SharedApp ? SharedApp.sendEmail : false,
              salesRepFirstName: userData.email
                ? userData.firstName
                : SharedApp
                ? SharedApp.submittingUser.firstName
                : "",
              salesRepLastName: userData.email
                ? userData.lastName
                : SharedApp
                ? SharedApp.submittingUser.lastName
                : "",
              salesRepEmail: userData.email
                ? userData.email
                : SharedApp ? SharedApp.submittingUser.email : "",
              salesRepPhone:
                userData.email
                  ? userData.phone
                  : SharedApp && SharedApp.submittingUser.phone
                  ? SharedApp.submittingUser.phone
                  : "",
            }}
            enableReinitialize={false}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={object({
              firstName: string().required(`Enter your first name`),
              lastName: string().required(`Enter your last name`),
              email: string()
                .email("Enter a valid email address, like j.doe@example.com")
                .required(`Enter the borrower's email address`),
              state: string().required(`Select state`),
              offer: string().required('Select an Offer'),
              mobile: string()
                .matches(phoneRegExp, "Enter a valid phone number")
                .required(`Enter a phone number`),
              loanAmount: string()
                        .required("Required")
                        .test("currenty", `Please enter a currency value`, val => validateMoneyInput(val))
                        .test("minAmount", `Amount must be at least ${numberToMoneyNoDecimal(minAmount)}`, val => moneyStringToNumber(val) > minAmount)
                        .test("maxAmount", `Amount must be less than ${numberToMoneyNoDecimal(maxAmount)}`, val => moneyStringToNumber(val) <= maxAmount)
            })}
            onSubmit={(values, { setFieldError }) => {
              console.log("onSubmit", values);
              values.loanAmount = moneyStringToNumber(values.loanAmount);
              if (!isSubmitOfferAllowed(values.offer, submitOfferContextRef.current)) {
                setFieldError("offer", submitOfferInvalidMessage);
                return;
              }
              handleClick(buildPaydown(values));
            }}
            showSummary={false}
          >
            <SyncSalesRepFields userData={userData} />
            <Card
              ref={formCardRef}
              sx={{
                width: 960,
                margin: "0 auto",
              }}
              className="card-form"
            >
              <Box sx={{ pl: 2, pr: 2, pb: 2 }}>
                <Heading variant="h5">
                  Complete this form to see an ESTIMATED payment scenario. Use the dollar amount suggested by your company representative.
                </Heading>
                <Box as="p" sx={{ mb: 2 }}>
                  &nbsp;
                </Box>
                {checkingSubmittingUser && (
                  <Box sx={{ width: "100%", mb: 2, mt: 2 }}>
                    <Loader
                      variation="linear"
                      emptyColor="#ffffff"
                      filledColor="#F58320"
                      className="loader"
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    pointerEvents: checkingSubmittingUser ? "none" : "auto",
                    opacity: checkingSubmittingUser ? 0.65 : 1,
                  }}
                >
                  <Box
                    sx={{
                      position: "static",
                      backgroundColor: GLP_BRAND.white,
                      pb: 2,
                      mb: 1,
                      "@media screen and (min-width: 640px)": {
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        borderBottom: "1px solid",
                        borderColor: "neutral.l3",
                      },
                    }}
                  >
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <TextInput
                      name="firstName"
                      label="First name"
                      autoComplete="off"
                      readOnly={fieldLocked(
                        formData.fname !== "" && formData.locked
                      )}
                    />
                    <TextInput
                      name="lastName"
                      label="Last name"
                      autoComplete="off"
                      readOnly={fieldLocked(
                        formData.lname !== "" && formData.locked
                      )}
                    />
                  </Grid>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <TextInput
                      name="email"
                      label="Email address"
                      autoComplete="off"
                      type="email"
                      readOnly={fieldLocked(
                        formData.email !== "" && formData.locked
                      )}
                    />
                    <TextInput
                      name="mobile"
                      label="Mobile number"
                      autoComplete="off"
                      type="phone"
                      description="Please enter 10 digit mobile number"
                      readOnly={fieldLocked(
                        formData.phone !== "" && formData.locked
                      )}
                    />
                  </Grid>
                  </Box>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <Box sx={{ width: "100%", mr: 5, mt: 2 }}>
                      <SingleSelect
                        dangerouslyOnChange={(e) => {
                          if (checkingSubmittingUser) return;
                          setSingleOffer(e);
                          const offer = offers.find((o) => getOfferRowId(o) === e);
                          if (offer) {
                            setOfferDesc(offer.productDescription);
                            setOfferName(offer.name);
                            getPayment(e, paymentAmount.amount);
                            const resolvedStates = resolveApplyStateEntries(
                              SharedApp.states,
                              offer.states
                            );
                            setStates(JSON.stringify(resolvedStates));
                            setStatesItems(toStateSelectItems(resolvedStates));
                            if (resolvedStates.length === 1) {
                              setSingleState(resolvedStates[0]);
                            }
                          }
                          if (isEmbedded) {
                            requestAnimationFrame(() => {
                              formCardRef.current?.scrollIntoView({
                                block: "start",
                                behavior: "smooth",
                              });
                              resetEmbeddedPageScroll();
                            });
                          }
                        }}
                        name="offer"
                        label={`Offer`}
                        items={offerItems}
                        readOnly={fieldLocked(
                          offerItems.find(
                            (offer) => offer.value === formData.offerId
                          ) !== undefined && formData.locked
                        )}
                      />
                      {/* <Text sx={{position: "relative", top: "-15px"}} variant="small">{offerDesc}</Text> */}
                    </Box>
                    {!statesItems.length && (
                      <Box sx={{ width: "30%", mr: 5, mt: 2 }}>
                       <SingleSelect
                        name="state"
                        label={"State"}
                        readOnly
                        description="Select an offer first"
                        size={20}
                        items={[]}
                      />
                      </Box>
                    )}
                    {statesItems.length > 0 && (
                    <Box sx={{ width: "30%", mr: 5, mt: 2 }}>
                      <ApplyStatesInput
                        statesItems={statesItems}
                        limitations={limitations}
                        minAmount={minAmount}
                        maxAmount={maxAmount}
                        setLoadingPayment={setLoadingPayment}
                        setCanApply={setCanApply}
                        readOnly={fieldLocked(
                          statesItems.find(st => st.value === formData.state) !== undefined &&
                            formData.locked
                        )}
                      />
                    </Box>
                  )}
                  </Grid>
                  <Grid columns={[1, 2]} gap={[0, 3]}>
                    <Box sx={{ mt: 2 }}>
                      <LoanAmountInput
                        isEmbedded={isEmbedded}
                        readOnly={fieldLocked(
                          formData.cost !== "" && formData.locked
                        )}
                        singleOffer={singleOffer}
                        limitations={limitations}
                        minAmount={minAmount}
                        maxAmount={maxAmount}
                        setPaymentAmount={setPaymentAmount}
                        setCanApply={setCanApply}
                      />
                     </Box>
                  </Grid>
                </Box>
                {payment && version === "2.0" &&
                <LumosCard
                    header={{
                      title: (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <img src={getCategory(SharedApp.category)} alt={getCategoryName(SharedApp.category)} style={styles.catLogo} />
                          {getCategoryName(SharedApp.category)} estimated loan details
                        </Box>
                      ),
                    }}
                  >
                <Box
                sx={{
                    pt: 3,
                    mt: 3,
                    borderTop: "1px solid",
                    borderTopColor: "neutral.l3",
                  }}>
                  <Grid columns={[1, 1, '1fr 2fr 2fr']}>
                    <Box
                      sx={{
                        borderRight: '1px solid',
                        borderColor: ['transparent', 'transparent', 'neutral.l3'],
                        mb: 3,
                      }}
                    >
                      <div>Loan amount</div>
                      <FormLoanAmountText />
                    </Box>
                    <Box
                      sx={{
                        borderRight: '1px solid',
                        borderColor: ['transparent', 'transparent', 'neutral.l3'],
                        mb: 3,
                        pr: 2,
                      }}
                    >
                      <DataList columns={1} items={productItems} />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                      <DataList columns={1} items={estimatedPayment} />
                    </Box>
                  </Grid>
                </Box>
                </LumosCard>}
                <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    pt: 3,
                    my: 3,
                    borderTop: "1px solid",
                    borderTopColor: "neutral.l3",
                    pointerEvents: checkingSubmittingUser ? "none" : "auto",
                    opacity: checkingSubmittingUser ? 0.65 : 1,
                  }}>
                   <ReCAPTCHA
                    ref={captchaRef}
                    sitekey="6Ld6Kb8hAAAAADN-z4PFwBU-mBk8HcGaWkKtmSs-"
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    onErrored={handleCaptchaExpired}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    my: 3,
                  }}
                >
                  {spinner && (
                    <>
                      <Button type="submit" sx={{ mt: 3, px: 5 }} disabled>
                        APPLY NOW
                      </Button>
                      <Spinner
                        title="loading"
                        size="32"
                        sx={{ mt: 3, px: 3, color: GLP_BRAND.maroon }}
                      />
                    </>
                  )}
                  {!spinner && canApply && !checkingSubmittingUser && (
                    <Button
                      type="submit"
                      sx={{
                        mt: 3,
                        px: 5,
                        backgroundColor: GLP_BRAND.maroon,
                        color: GLP_BRAND.white,
                        border: "none",
                        "&:hover": { backgroundColor: GLP_BRAND.maroonHover },
                      }}
                    >
                      APPLY NOW
                    </Button>
                  )}
                  {!spinner && (!canApply || checkingSubmittingUser) && (
                    <Button type="submit" sx={{ mt: 3, px: 5 }} disabled>
                      APPLY NOW
                    </Button>
                  )}
                </Box>
              </Box>
            </Card>
          </Form>
          )}
        </Box>
      )}
    </Box>
  );
};

const styles = {
  heading: {
    backgroundColor: GLP_BRAND.maroon,
  },
  loader: {
    height: "5rem"
  },
  period: {
     display: 'inline', fontSize: '70%', fontWeight: 'normal'
  },
  catLogo: {
    width: 24,
    position: "relative",
    top: 0,
    margin: "0 10px",
  }
};

export default Home;
